import React, { useEffect, useState, useMemo } from "react";
import { Panel, PanelGroup } from "react-resizable-panels";
import * as THREE from "three";
import ZoomControl from "./components/ZoomControl";

// Workspace architecture imports
import {
  useWorkspaceState,
  useNodeOperations,
  useBeamOperations,
  useSelectionOperations,
  useViewportControls,
  useWorkspaceKeyboardShortcuts,
} from "./hooks";

import { useSelectionIntegration } from "./hooks/operations/useSelectionIntegration";
import { useEnhancedNodeOperations } from "./hooks/operations/useEnhancedNodeOperations";
import { useEnhancedBeamOperations } from "./hooks/operations/useEnhancedBeamOperations";

import {
  Canvas3D,
  ViewportOverlays,
  WorkspaceDialogs,
  CollapsiblePanel,
} from "./components";
import { ToggleButton } from "./components/ToggleButton";

// UI additions for context menu & node loads
import ContextMenu, { ContextMenuItem } from "../ui/ContextMenu";
import { LoadInputModal } from "../ui/LoadInputModal";
import NodeLoadIndicators from "../truss/NodeLoadIndicators";

// Support system imports
import { useSupportStore } from "../../store/useSupportStore";
import { SupportCubeCollection } from "../analysis/SupportCube3D";
import { EnhancedNodeInteractionComponents } from "./EnhancedNodeInteractionComponents";
import { EnhancedBeamInteractionComponents } from "./EnhancedBeamInteractionComponents";
import { BeamSectionVisualization } from "../modeling/BeamSectionVisualization";
import { useBeamSectionStore } from "../../hooks/useBeamSection";

import { STAADWorkspaceModernProps, ViewType } from "./utils";

// Store imports
import { useModelStore } from "../../store/useModelStore";
import { useCopyPaste } from "../../hooks/useCopyPaste";
import { useUIStore } from "../../store/useUIStore";
import { usePropertyStore } from "../../store/usePropertyStore";

// UI component imports
import { NodesDataTable } from "../ui/tables/NodesDataTable";
import { BeamsDataTable } from "../ui/tables/BeamsDataTable";
import { BeamElement } from "../modeling/StructuralElements/BeamElement";
import { DottedPreviewLine } from "../ui/controls/DottedPreviewLine";
import { SceneControlsUI } from "../workspace/SceneControls";
import { CreatePerpBeamButton } from "../ui/controls/CreatePerpBeamButton";
import { SelectionInfoDisplay } from "./components/ui/SelectionInfoDisplay";
import {
  TrussTool,
  TrussIndicator,
  TrussConfigPanel,
  EnhancedTrussVisibilityToggle,
  BeamTrussIndicator,
  BeamTrussConfigPanel,
  DualPressureIndicator,
} from "../truss";
import { LoadVisualization3D } from "../loading/LoadVisualization3D";
import { TwistedLoadVisualization3D } from "../loading/TwistedLoadVisualization3D";
import { BMDVisualization } from "../analysis/BMDVisualization";
import { getBMDDataForBeam } from "../../utils/bmdUtils";
import { useTrussStore } from "../../hooks/useTrussManagement";
import { useBeamTrussStore } from "../../hooks/useBeamTrussManagement";
import { OffsetInputDialog } from "../ui/dialogs";
import { AnalysisPanel } from "../analysis/AnalysisPanel";
import { SectionPropertiesTable } from "../ui/tables/SectionPropertiesTable";
import { SectionPropertiesConfigDialog } from "../ui/dialogs/SectionPropertiesConfigDialog";
import {
  isValidForPerpBeam,
  calculatePerpPoint,
} from "../../utilities/PerpBeamUtils";

import { Info } from "lucide-react";
import { ViewInfoPanel } from "./components/ui/ViewInfoPanel";
import { LoadcaseDataTable } from "../ui/tables/LoadcaseDataTable";
import { SupportsDataTable } from "../ui/tables";
import { useDeleteKeyHandler } from "@/hooks/useDeleteKeyHandler";
import { useClickToSelect } from "@/hooks/useClickToSelect";

import { scaleToGrid, getScaleFromRange, getSupportVisualScale, getLoadVisualScale } from "@/utils/gridScale";
import { getAutoScaleRange } from "@/utils/autoScale";
import { useLoadcaseGroupStore } from "@/store/useLoadcaseGroupStore";

/**
 * Refactored STAADWorkspace3D Component
 *
 * Enterprise-grade, maintainable architecture with zero functional regression
 * Extracted from 2500+ line monolithic component into focused hooks and components
 */
export const STAADWorkspace3D: React.FC<STAADWorkspaceModernProps> = ({
  currentView,
  onViewChange,
}) => {
  // 1. Initialize stores with defensive programming
  const modelStore = useModelStore();
  const copyPasteStore = useCopyPaste();
  const supports = useSupportStore((state) => state.supports);
  const uiStore = useUIStore();
  const supportStore = useSupportStore();

  const scaleRange = useUIStore((s) => s.scaleRange);
  const setScaleRange = useUIStore((s) => s.setScaleRange);
  const scaleFactor = getScaleFromRange(scaleRange);
  const [autoScale, setAutoScale] = useState<boolean>(() => {
    const stored = localStorage.getItem("auto_scale_enabled");
    return stored === null ? true : stored === "true";
  });

  useDeleteKeyHandler(); // Delete component handler
  useClickToSelect(); // Select componenet handler

  // Early return if stores not initialized
  if (!modelStore || !copyPasteStore || !uiStore) {
    console.warn("STAADWorkspace3D: Stores not initialized");
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading workspace...</div>
      </div>
    );
  }

  // 2. Extract state management
  const { state, callbacks } = useWorkspaceState();

  // Property configuration dialog state
  const [isPropertyDialogOpen, setIsPropertyDialogOpen] = useState(false);

  // 3. Initialize truss stores at top level
  const trussStore = useTrussStore();
  const beamTrussStore = useBeamTrussStore();

  // Loadcase filtering - only show loads belonging to active loadcase
  const {
    activeLoadcaseName,
    getBeamLoadIdsForLoadcase,
    getNodeLoadIdsForLoadcase,
  } = useLoadcaseGroupStore();
  const activeBeamLoadIds = activeLoadcaseName
    ? getBeamLoadIdsForLoadcase(activeLoadcaseName)
    : [];
  const activeNodeLoadIds = activeLoadcaseName
    ? getNodeLoadIdsForLoadcase(activeLoadcaseName)
    : [];

  // Get grouped truss presets to map IDs to presetNames for filtering
  const groupedTrussPresets = trussStore.getGroupedTruss();
  // Build a set of presetNames that belong to the active loadcase
  const activeNodeLoadPresetNames = useMemo(() => {
    const names = new Set<string>();
    groupedTrussPresets.forEach((preset: any) => {
      if (activeNodeLoadIds.includes(preset.id)) {
        // Add the presetName (or name as fallback)
        names.add(preset.name);
      }
    });
    return names;
  }, [groupedTrussPresets, activeNodeLoadIds]);

  const {
    handleNodeClick,
    handleBeamClick,
    isBoxSelectionEnabled,
    getSelectionFilter,
  } = useClickToSelect();

  // Initialize section and support stores for visibility toggles
  const beamSectionStore = useBeamSectionStore();
  const { isVisible: isSectionVisible, toggleVisible: toggleSectionVisible } =
    beamSectionStore;
  const { isVisible: isSupportVisible, toggleVisible: toggleSupportVisible } =
    supportStore;

  // Add beam truss configuration state
  const [beamTrussConfigState, setBeamTrussConfigState] = useState<{
    isOpen: boolean;
    beamId: string | null;
    position: { x: number; y: number } | null;
  }>({
    isOpen: false,
    beamId: null,
    position: null,
  });

  // View Info Panel state
  const [isViewInfoPanelVisible, setIsViewInfoPanelVisible] = useState(false);

  // 3. Extract business logic operations
  const nodeOperations = useNodeOperations(
    state,
    callbacks,
    modelStore,
    uiStore
  );
  const beamOperations = useBeamOperations(
    state,
    callbacks,
    modelStore,
    uiStore
  );
  const selectionOperations = useSelectionOperations(
    state,
    callbacks,
    modelStore,
    uiStore,
    isBoxSelectionEnabled
  );

  // Enhanced node operations for context menu interactions
  const enhancedNodeOperations = useEnhancedNodeOperations();

  // Enhanced beam operations for context menu interactions
  const enhancedBeamOperations = useEnhancedBeamOperations();

  // 4. Extract UI controls
  const viewportControls = useViewportControls(
    state,
    { currentView, onViewChange },
    modelStore.nodes || [],
    modelStore.beams || []
  );

  // 5. Extract keyboard shortcuts
  useWorkspaceKeyboardShortcuts(
    state,
    callbacks,
    {
      nodes: nodeOperations,
      beams: beamOperations,
      selection: selectionOperations,
    },
    modelStore,
    uiStore,
    copyPasteStore
  );

  // 6. Initialize selection integration
  useSelectionIntegration();

  // 6. Handle view changes from parent
  useEffect(() => {
    if (currentView) {
      viewportControls.switchView(currentView);
    }
  }, [currentView, viewportControls]);

  // 7. Destructure store values with defaults
  const {
    nodes = [],
    beams = [],
    selectedNodeIds = [],
    selectedBeamIds = [],
    hoveredBeam,
  } = modelStore;

  const {
    isOffsetDialogOpen = false,
    selectedCount = 0,
    hideOffsetDialog,
    pasteWithOffset,
    canCopyPaste = false,
  } = copyPasteStore;

  // 8. Handle view change with type conversion
  const handleViewChange = (view: string) => {
    if (
      view === "front" ||
      view === "top" ||
      view === "side" ||
      view === "isometric"
    ) {
      onViewChange(view as ViewType);
    }
  };
  useEffect(() => {
    if (!autoScale || nodes.length === 0) return;

    const newRange = getAutoScaleRange(nodes);

    // Update scale range
    setScaleRange(newRange);
  }, [nodes]);

  // Listen for auto scale toggle event to trigger recalculation
  useEffect(() => {
    const handleAutoScaleRecalculation = () => {
      const storedAutoScale = localStorage.getItem("auto_scale_enabled");
      const isAutoScaleEnabled = storedAutoScale === "true";
      setAutoScale(isAutoScaleEnabled);

      if (isAutoScaleEnabled && nodes.length > 0) {
        const newRange = getAutoScaleRange(nodes);
        setScaleRange(newRange);
      }
    };

    window.addEventListener(
      "triggerAutoScaleRecalculation",
      handleAutoScaleRecalculation
    );
    return () => {
      window.removeEventListener(
        "triggerAutoScaleRecalculation",
        handleAutoScaleRecalculation
      );
    };
  }, [nodes, setScaleRange]);

  // ===== COMPUTE NODE LOADS FOR RENDERING =====
  const { computedNodeMomentLoads, computedNodePerpLoads } = useMemo(() => {
    const nodeMomentLoads: Record<string, { x: number; y: number; z: number }> =
      {};
    const nodePerpLoads: Record<string, { x: number; y: number; z: number }> =
      {};

    // Process perpendicular loads
    trussStore.nodeTrusses
      .filter((t) => {
        if (t.type !== "perpendicular") return false;
        if (t.visible === false) return false;
        if (!activeLoadcaseName || activeLoadcaseName === "None") return true;
        const trussPresetName = t.presetName ?? t.label;
        return activeNodeLoadPresetNames.has(trussPresetName);
      })
      .forEach((truss) => {
        console.log(
          "🔍 Processing perp truss:",
          truss.id,
          "rotationValue:",
          truss.rotationValue
        );

        truss.nodeIds.forEach((nodeId) => {
          const numericNodeId = Number(nodeId);
          if (!nodePerpLoads[numericNodeId]) {
            nodePerpLoads[numericNodeId] = { x: 0, y: 0, z: 0 };
          }

          const value = truss.rotationValue ?? 0;
          const planeLower = (truss.plane || "Z").toLowerCase();

          if (planeLower === "x") nodePerpLoads[numericNodeId].x += value;
          else if (planeLower === "y") nodePerpLoads[numericNodeId].y += value;
          else if (planeLower === "z") nodePerpLoads[numericNodeId].z += value;
        });
      });

    // Process moment loads
    trussStore.nodeTrusses
      .filter((t) => {
        if (t.type !== "regular") return false;
        if (t.visible === false) return false;
        if (!activeLoadcaseName || activeLoadcaseName === "None") return true;
        const trussPresetName = t.presetName ?? t.label;
        return activeNodeLoadPresetNames.has(trussPresetName);
      })
      .forEach((truss) => {
        truss.nodeIds.forEach((nodeId) => {
          const numericNodeId = Number(nodeId);

          if (!nodeMomentLoads[numericNodeId]) {
            nodeMomentLoads[numericNodeId] = { x: 0, y: 0, z: 0 };
          }
          const value = truss.rotationValue ?? 0;
          const planeLower = (truss.plane || "Y").toLowerCase();

          if (planeLower === "x") nodeMomentLoads[numericNodeId].x += value;
          else if (planeLower === "y")
            nodeMomentLoads[numericNodeId].y += value;
          else if (planeLower === "z")
            nodeMomentLoads[numericNodeId].z += value;
        });
      });

    return {
      computedNodeMomentLoads: nodeMomentLoads,
      computedNodePerpLoads: nodePerpLoads,
    };
  }, [trussStore.nodeTrusses, activeLoadcaseName, activeNodeLoadPresetNames]);

  // 9. Main render - preserving exact JSX hierarchy from original
  return (
    <div className="h-screen flex flex-col">
      {/* Header Toolbar - Hidden, selection now controlled via ViewMenu */}
      <div className="hidden bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-lg font-semibold">Fema</h1>
          </div>

          <div className="flex items-center space-x-4">
            {/* Select dropdown removed - selection now controlled via ViewMenu buttons */}

            <SceneControlsUI
              currentView={currentView}
              isGridVisible={state.isGridVisible}
              onViewChange={handleViewChange}
              onGridToggle={callbacks.setIsGridVisible}
            />

            {/* View Info Panel Toggle Button */}
            <button
              onClick={() => setIsViewInfoPanelVisible(!isViewInfoPanelVisible)}
              className={`px-3 py-1 text-sm rounded-md transition-colors flex items-center space-x-1
                ${
                  isViewInfoPanelVisible
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                }`}
              title="Toggle View Information Panel"
            >
              <Info className="w-4 h-4" />
              <span>View Info</span>
            </button>

            {/* Zoom control moved to header to avoid overlap with right pane dropdowns */}
            <ZoomControl inline />

            {/* Create Node Button */}
            <button
              className={`px-3 py-1 text-sm rounded-md transition-colors
                        ${
                          state.isNodeCreationMode
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
              onClick={() => {
                callbacks.setIsNodeCreationMode(!state.isNodeCreationMode);
                callbacks.setIsBeamCreationMode(false); // Disable beam mode when enabling node mode
              }}
              title="Create Node (N)"
            >
              <span className="flex items-center">
                <span className="mr-1">●</span> Create Node
              </span>
            </button>

            {/* Create Beam Button */}
            <button
              className={`px-3 py-1 text-sm rounded-md transition-colors
                        ${
                          state.isBeamCreationMode
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
              onClick={() => {
                if (!state.isBeamCreationMode) {
                  callbacks.setIsBeamCreationMode(true);
                  callbacks.setIsNodeCreationMode(false);
                  modelStore.startBeamCreation();
                } else {
                  callbacks.setIsBeamCreationMode(false);
                  modelStore.cancelBeamCreation();
                }
              }}
              title="Create Beam (B)"
            >
              <span className="flex items-center">
                <span className="mr-1">━</span> Create Beam
              </span>
            </button>

            {/* Prep Mode Button */}
            <button
              className={`px-3 py-1 text-sm rounded-md transition-colors
                        ${
                          modelStore.isPrepMode
                            ? "bg-purple-600 text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
              onClick={() => {
                if (!modelStore.isPrepMode) {
                  modelStore.startPrepMode();
                  callbacks.setIsNodeCreationMode(false);
                  callbacks.setIsBeamCreationMode(false);
                } else {
                  modelStore.cancelPrepMode();
                }
              }}
              title="Perpendicular Beam Creation (P)"
            >
              <span className="flex items-center">
                <span className="mr-1">_|_</span> Prep Mode
              </span>
            </button>

            <TrussTool />
            <EnhancedTrussVisibilityToggle />

            {/* Section Visibility Toggle */}
            <button
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                isSectionVisible
                  ? "bg-purple-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
              onClick={toggleSectionVisible}
              title="Toggle Sections Visibility"
            >
              <span className="flex items-center">
                <span className="mr-1">📦</span> Section
              </span>
            </button>

            {/* Support Visibility Toggle */}
            <button
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                isSupportVisible
                  ? "bg-green-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
              onClick={toggleSupportVisible}
              title="Toggle Supports Visibility"
            >
              <span className="flex items-center">
                <span className="mr-1">🔧</span> Supports
              </span>
            </button>

            {/* Node ID Toggle Button */}
            <ToggleButton
              label="N_ID"
              isActive={uiStore.showNodeIds}
              onClick={() => {
                const oldState = uiStore.showNodeIds;
                uiStore.toggleNodeIds();
                const newState = !oldState;
                uiStore.showNotification(
                  "info",
                  newState ? "Node IDs Shown" : "Node IDs Hidden",
                  newState
                    ? "All node IDs are now displayed"
                    : "All node IDs are now hidden"
                );
              }}
              title={uiStore.showNodeIds ? "Hide Node IDs" : "Show Node IDs"}
            />
            {/* Beam ID Toggle Button */}
            <ToggleButton
              label="B_ID"
              isActive={uiStore.showBeamIds}
              onClick={() => {
                const oldState = uiStore.showBeamIds;
                uiStore.toggleBeamIds();
                const newState = !oldState;
                uiStore.showNotification(
                  "info",
                  newState ? "Beam IDs Shown" : "Beam IDs Hidden",
                  newState
                    ? "All beam IDs are now displayed"
                    : "All beam IDs are now hidden"
                );
              }}
              title={uiStore.showBeamIds ? "Hide Beam IDs" : "Show Beam IDs"}
            />
            {/* Zoom-to-cursor is enabled by default; toggle removed per requirements */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex height-860px overflow-y-auto">
        <PanelGroup direction="horizontal">
          {/* Center Panel - 3D Viewport */}
          <Panel defaultSize={60} minSize={40}>
            <div className="h-full bg-gray-100 dark:bg-gray-800 relative">
              {/* Viewport Overlays */}
              <ViewportOverlays state={state} nodes={nodes} />

              {/* Selection Information Display */}
              <SelectionInfoDisplay
                selectedNodeIds={selectedNodeIds}
                selectedBeamIds={selectedBeamIds}
                selectedNodesForPerp={
                  modelStore.isPrepMode ? selectedNodeIds : []
                }
                selectedBeamForPerp={
                  modelStore.isPrepMode ? modelStore.referenceBeamId : null
                }
              />

              {/* Main Canvas */}
              <Canvas3D
                currentView={currentView}
                isGridVisible={state.isGridVisible}
                isNodeCreationMode={state.isNodeCreationMode}
                isBeamCreationMode={state.isBeamCreationMode}
                zoomToCursor={state.zoomToCursor}
                isDragging={state.isDragging}
                isSelectionMode={state.isSelectionMode}
                isCtrlPressed={state.isCtrlPressed}
                onGridClick={nodeOperations.handlePlaneClick}
                onSelectionMouseDown={selectionOperations.handleCanvasMouseDown}
                onSelectionMouseMove={selectionOperations.handleCanvasMouseMove}
                onSelectionMouseUp={selectionOperations.handleCanvasMouseUp}
              >
                {/* Nodes */}
                {nodes.map((node) => {
                  const rawPosition =
                    state.isDragging &&
                    state.draggedNodeId === node.id &&
                    state.currentDragPosition
                      ? state.currentDragPosition
                      : node;

                  const position: [number, number, number] = [
                    scaleToGrid(rawPosition.x, scaleFactor),
                    scaleToGrid(rawPosition.y, scaleFactor),
                    scaleToGrid(rawPosition.z, scaleFactor),
                  ];

                  const isSelected = selectedNodeIds.includes(node.id);
                  const isHovered = state.hoveredNodeId === node.id;

                  const labelOffset = 1.2;

                  return (
                    <mesh
                      key={node.id}
                      position={position}
                      onPointerDown={() => {
                        // Node dragging disabled - no action taken
                      }}
                      onPointerMove={() => {
                        // Node dragging disabled - no action taken
                      }}
                      onPointerUp={() => {
                        // Node dragging disabled - no action taken
                      }}
                      onPointerEnter={() =>
                        nodeOperations.handleNodeHover(node.id)
                      }
                      onPointerLeave={() =>
                        nodeOperations.handleNodeHover(null)
                      }
                      onClick={(event) => {
                        event.stopPropagation();

                        // Handle Prep Mode node selection
                        if ((window as any).__supportAssignCursorMode) {
                          event.stopPropagation();

                          // Call the global handler to assign support
                          if (
                            (window as any).__handleNodeClickForSupportAssign
                          ) {
                            (window as any).__handleNodeClickForSupportAssign(
                              node.id
                            );
                          }

                          return; // Prevent all other node click behaviors
                        }

                        if ((window as any).__propertyAssignCursorMode) {
                          event.stopPropagation();

                          if (
                            (window as any).__handleNodeClickForPropertyAssign
                          ) {
                            (window as any).__handleNodeClickForPropertyAssign(
                              node.id
                            );
                          }

                          return;
                        }

                        if (state.isBeamCreationMode) {
                          if (!modelStore.beamCreationFirstNodeId) {
                            // First node click in beam creation mode
                            modelStore.setBeamCreationFirstNode(node.id);
                          } else {
                            modelStore.addBeam(
                              modelStore.beamCreationFirstNodeId,
                              node.id
                            );
                            modelStore.cancelBeamCreation();
                          }
                        } else {
                          // Check if truss tool is active

                          if (trussStore.activeTool) {
                            // For Three.js events, we need to get the canvas element
                            const canvas =
                              event.eventObject?.parent?.parent?.parent
                                ?.userData?.canvas ||
                              document.querySelector("canvas") ||
                              event.nativeEvent?.target;

                            let rect = { left: 200, top: 200 }; // Default position

                            if (
                              canvas &&
                              typeof canvas.getBoundingClientRect === "function"
                            ) {
                              rect = canvas.getBoundingClientRect();
                            }

                            uiStore.openTrussConfigPanel(node.id, {
                              x: rect.left + window.scrollX + 50, // Add offset from cursor
                              y: rect.top + window.scrollY + 50,
                            });
                          } else {
                            // Normal node selection
                            handleNodeClick(node.id, event);
                          }
                        }
                      }}
                      onContextMenu={(event) => {
                        // Use enhanced node operations for right-click context menu
                        enhancedNodeOperations.handleEnhancedNodeRightClick(
                          node.id,
                          event
                        );
                      }}
                    >
                      <sphereGeometry args={[0.15, 16, 16]} />{" "}
                      {/* Reduced node size from 0.5 to 0.15 */}
                      <meshStandardMaterial
                        color={
                          node.color ||
                          (isSelected
                            ? "#ff6b6b"
                            : isHovered
                              ? "#4ecdc4"
                              : "#000000")
                        }
                        transparent
                        opacity={isHovered ? 0.8 : 1.0}
                      />
                      {/* Node label */}
                      {state.showNodeLabels && (
                        <mesh position={[0, labelOffset, 0]}>
                          {/* Label implementation would go here */}
                        </mesh>
                      )}
                    </mesh>
                  );
                })}

                {/* Support Cubes with DOF Indicators */}
                <SupportCubeCollection
                  supports={supports}
                  nodePositions={
                    new Map(
                      nodes.map((node) => [
                        node.id.toString(),
                        new THREE.Vector3(
                          scaleToGrid(node.x, scaleFactor),
                          scaleToGrid(node.y, scaleFactor),
                          scaleToGrid(node.z, scaleFactor)
                        ),
                      ])
                    )
                  }
                  visible={supportStore.isVisible}
                  showDOF={true}
                  scaleFactor={getSupportVisualScale(scaleRange)}
                />

                <NodeLoadIndicators
                  nodes={(() => {
                    const scaledNodes = nodes.map((n) => ({
                      id: String(n.id),
                      x: scaleToGrid(n.x, scaleFactor),
                      y: scaleToGrid(n.y, scaleFactor),
                      z: scaleToGrid(n.z, scaleFactor),
                    }));

                    // Check if IDs match
                    const nodeIds = scaledNodes.map((n) => n.id);
                    const perpLoadNodeIds = Object.keys(computedNodePerpLoads);
                    const mismatches = perpLoadNodeIds.filter(
                      (id) => !nodeIds.includes(id)
                    );
                    if (mismatches.length > 0) {
                      console.error(
                        "❌ [NodeLoadIndicators] ID MISMATCH! Perp loads for nodes:",
                        mismatches,
                        "but those nodes don't exist in scaled nodes"
                      );
                    }

                    return scaledNodes;
                  })()}
                  nodeMomentLoads={computedNodeMomentLoads}
                  nodePerpLoads={computedNodePerpLoads}
                />

                {/* Beams */}
                {beams.map((beam) => {
                  // Find start and end nodes for the beam
                  const startNode = nodes.find(
                    (n) => n.id === beam.startNodeId
                  );
                  const endNode = nodes.find((n) => n.id === beam.endNodeId);

                  if (!startNode || !endNode) return null;

                  const startPoint = new THREE.Vector3(
                    scaleToGrid(startNode.x, scaleFactor),
                    scaleToGrid(startNode.y, scaleFactor),
                    scaleToGrid(startNode.z, scaleFactor)
                  );

                  const endPoint = new THREE.Vector3(
                    scaleToGrid(endNode.x, scaleFactor),
                    scaleToGrid(endNode.y, scaleFactor),
                    scaleToGrid(endNode.z, scaleFactor)
                  );

                  return (
                    <BeamElement
                      key={beam.id}
                      beamId={beam.id}
                      startPoint={startPoint}
                      endPoint={endPoint}
                      isSelected={selectedBeamIds.includes(beam.id)}
                      isNodeModeActive={state.isNodeCreationMode}
                      onBeamClick={(beamId, point) => {
                        const filter = getSelectionFilter();
                        if (filter === "nodes") {
                          return; // Don't process beam clicks when only nodes are selectable
                        }

                        // Handle Divide Beam Mode with existence check
                        if (modelStore.isDivideBeamMode) {
                          // Verify beam still exists before attempting division
                          const beamExists = modelStore.beams.find(
                            (b) => String(b.id) === String(beamId)
                          );

                          if (!beamExists) {
                            console.warn(
                              "⚠️ Beam already divided or removed:",
                              beamId
                            );
                            uiStore.showNotification?.(
                              "warning",
                              "Beam Already Divided",
                              `Beam ${beamId} was already split. Click a different beam.`
                            );
                            return;
                          }

                          const success =
                            modelStore.divideBeamAtMidpoint(beamId);
                          if (success) {
                            uiStore.showNotification?.(
                              "success",
                              "Beam Divided",
                              `Beam ${beamId} split at midpoint into 2 new beams`
                            );
                          } else {
                            console.error("Failed to divide beam");
                            uiStore.showNotification?.(
                              "error",
                              "Division Failed",
                              `Could not divide beam ${beamId}`
                            );
                          }
                          return; // Stop further processing
                        }

                        // Check for Apply by Click mode first
                        const propertyStore = usePropertyStore.getState();

                        if (
                          propertyStore.applyByClickMode &&
                          propertyStore.activePropertyIdForClick
                        ) {
                          window.dispatchEvent(
                            new CustomEvent("beamClicked", {
                              detail: { beamId },
                            })
                          );
                          return;
                        }

                        // Handle Prep Mode beam selection
                        if (modelStore.isPrepMode) {
                          modelStore.toggleBeamSelection(beamId);
                          return;
                        }

                        // Check if truss tool is active for beam distributed loads
                        if (trussStore.activeTool) {
                          console.log(
                            "🎯 Truss tool active, showing beam truss config for beam:",
                            beamId
                          );

                          // Calculate position for the configuration panel
                          const canvas = document.querySelector("canvas");
                          let rect = { left: 200, top: 200 }; // Default position

                          if (
                            canvas &&
                            typeof canvas.getBoundingClientRect === "function"
                          ) {
                            rect = canvas.getBoundingClientRect();
                          }

                          setBeamTrussConfigState({
                            isOpen: true,
                            beamId: beamId,
                            position: {
                              x: rect.left + window.scrollX + 50,
                              y: rect.top + window.scrollY + 50,
                            },
                          });
                          return;
                        }

                        handleBeamClick(beamId, { nativeEvent: point });
                      }}
                      onBeamHover={beamOperations.handleBeamHover}
                      onBeamRightClick={
                        enhancedBeamOperations.handleEnhancedBeamRightClick
                      }
                      onBeamDoubleClick={
                        enhancedBeamOperations.handleEnhancedBeamDoubleClick
                      }
                      showArrow={hoveredBeam === beam.id}
                      material={beam.material || "steel"}
                      highlightColor={
                        hoveredBeam === beam.id
                          ? modelStore.isDivideBeamMode
                            ? "#ff8c00"
                            : "#4ecdc4"
                          : undefined
                      }
                    />
                  );
                })}

                {/* Beam Truss Indicators (Distributed Loads) */}
                {beams.map((beam) => {
                  // Find start and end nodes for the beam
                  const startNode = nodes.find(
                    (n) => n.id === beam.startNodeId
                  );
                  const endNode = nodes.find((n) => n.id === beam.endNodeId);

                  if (!startNode || !endNode) return null;

                  const startPoint = new THREE.Vector3(
                    scaleToGrid(startNode.x, scaleFactor),
                    scaleToGrid(startNode.y, scaleFactor),
                    scaleToGrid(startNode.z, scaleFactor)
                  );
                  const endPoint = new THREE.Vector3(
                    scaleToGrid(endNode.x, scaleFactor),
                    scaleToGrid(endNode.y, scaleFactor),
                    scaleToGrid(endNode.z, scaleFactor)
                  );

                  // Get beam trusses for this beam - filtered by active loadcase
                  const allBeamTrusses = beamTrussStore.getTrussesForBeam(
                    beam.id
                  );
                  const beamTrusses = activeLoadcaseName
                    ? allBeamTrusses.filter((bt) =>
                        activeBeamLoadIds.includes(bt.id)
                      )
                    : allBeamTrusses; // Show all if no loadcase selected

                  return beamTrusses.map((beamTruss) => {
                    // Handle load visualization for new load types (distributed_uniform, distributed_variable, concentrated_load, twisted_load)
                    if (
                      beamTruss.type === "distributed_uniform" ||
                      beamTruss.type === "distributed_variable" ||
                      beamTruss.type === "concentrated_load" ||
                      beamTruss.type === "center_pressure"
                    ) {
                      return (
                        <LoadVisualization3D
                          key={beamTruss.id}
                          beamTruss={beamTruss}
                          startPoint={startPoint}
                          endPoint={endPoint}
                          visible={beamTruss.visible}
                          scaleFactor={scaleRange}
                        />
                      );
                    }

                    // Handle twisted load visualization
                    if (beamTruss.type === "twisted_load") {
                      return (
                        <TwistedLoadVisualization3D
                          key={beamTruss.id}
                          beamTruss={beamTruss}
                          startPoint={startPoint}
                          endPoint={endPoint}
                          visible={beamTruss.visible}
                          scaleFactor={scaleRange}
                        />
                      );
                    }

                    if (!beamTruss.visible) return null;

                    // Handle dual pressure loads
                    if (beamTruss.type === "dual_pressure") {
                      return (
                        <DualPressureIndicator
                          key={beamTruss.id}
                          startPoint={startPoint}
                          endPoint={endPoint}
                          leftPressureValue={beamTruss.leftPressureValue || 0}
                          rightPressureValue={beamTruss.rightPressureValue || 0}
                          baseArrowLength={beamTruss.baseArrowLength}
                          visible={beamTruss.visible}
                          scaleFactor={scaleRange}
                        />
                      );
                    }

                    // Handle distributed and pressure loads with old indicator (fallback)
                    // Calculate arrow length based on pressure (length increases, not size)
                    const arrowLength = beamTrussStore.calculateArrowLength(
                      beamTruss.pressureValue
                    );

                    // Calculate arrow count based on beam length
                    const beamLength = startPoint.distanceTo(endPoint);
                    const arrowCount =
                      beamTrussStore.calculateArrowCount(beamLength);

                    return (
                      <BeamTrussIndicator
                        key={beamTruss.id}
                        startPoint={startPoint}
                        endPoint={endPoint}
                        arrowCount={arrowCount}
                        arrowLength={arrowLength}
                        color={beamTruss.color}
                        visible={beamTruss.visible}
                        scaleFactor={scaleRange}
                      />
                    );
                  });
                })}

                {/* BMD (Bending Moment Diagram) Visualizations */}
                {beams.map((beam) => {
                  // Check if BMD is visible for this beam
                  const isBMDVisible = uiStore.beamBMDVisibility[beam.id];
                  if (!isBMDVisible) return null;

                  // Find start and end nodes for the beam
                  const startNode = nodes.find(
                    (n) => n.id === beam.startNodeId
                  );
                  const endNode = nodes.find((n) => n.id === beam.endNodeId);
                  if (!startNode || !endNode) return null;

                  // Calculate beam length
                  const length = Math.sqrt(
                    Math.pow(endNode.x - startNode.x, 2) +
                      Math.pow(endNode.y - startNode.y, 2) +
                      Math.pow(endNode.z - startNode.z, 2)
                  );

                  // Generate BMD data - use custom data if available, otherwise fallback to hardcoded
                  const beamProperties = {
                    id: beam.id,
                    length: length,
                    startPoint: new THREE.Vector3(
                      scaleToGrid(startNode.x, scaleFactor),
                      scaleToGrid(startNode.y, scaleFactor),
                      scaleToGrid(startNode.z, scaleFactor)
                    ),
                    endPoint: new THREE.Vector3(
                      scaleToGrid(endNode.x, scaleFactor),
                      scaleToGrid(endNode.y, scaleFactor),
                      scaleToGrid(endNode.z, scaleFactor)
                    ),
                    supportType: "simply-supported" as const,
                  };

                  // Get custom BMD data from UI store if available
                  const customBMDData = uiStore.customBMDData[beam.id];
                  const bmdData = getBMDDataForBeam(
                    beamProperties,
                    customBMDData
                  );

                  return (
                    <BMDVisualization
                      key={`bmd-${beam.id}`}
                      bmdData={bmdData}
                      beam={beamProperties}
                      visible={true}
                      scale={0.1} // Scale factor 3cm per kN·m
                      fillColor="#FFA500" // Orange
                      outlineColor="#F5330C" // Dark golden rod - much darker and bolder
                      opacity={0.7}
                    />
                  );
                })}

                {/* Section Visualizations */}
                <BeamSectionVisualization />

                {/* Dotted preview lines for prep mode */}
                {modelStore.isPrepMode &&
                  modelStore.referenceBeamId &&
                  selectedNodeIds.length > 0 &&
                  (() => {
                    // Find the reference beam
                    const refBeam = beams.find(
                      (b) => b.id === modelStore.referenceBeamId
                    );
                    if (!refBeam) return null;

                    // Find the start and end nodes of the reference beam
                    const refStartNode = nodes.find(
                      (n) => n.id === refBeam.startNodeId
                    );
                    const refEndNode = nodes.find(
                      (n) => n.id === refBeam.endNodeId
                    );
                    if (!refStartNode || !refEndNode) return null;

                    // No need for helper function - use imported utility

                    // Create preview lines only for valid nodes
                    return selectedNodeIds
                      .map((nodeId) => {
                        const targetNode = nodes.find((n) => n.id === nodeId);
                        if (!targetNode) return null;

                        // Skip nodes that are out of scope using utility function
                        if (
                          !isValidForPerpBeam(
                            targetNode,
                            refStartNode,
                            refEndNode
                          )
                        ) {
                          return null;
                        }

                        // Calculate perpendicular point using utility function
                        const { point: perpPoint } = calculatePerpPoint(
                          targetNode,
                          refStartNode,
                          refEndNode
                        );
                        const nodePoint = new THREE.Vector3(
                          targetNode.x,
                          targetNode.y,
                          targetNode.z
                        );

                        // Create preview line from target node to perpendicular point
                        return (
                          <DottedPreviewLine
                            key={`prep-preview-${nodeId}`}
                            startPoint={nodePoint}
                            endPoint={perpPoint}
                            color="#ffa500"
                            dashSize={0.3}
                            gapSize={0.2}
                            lineWidth={2}
                          />
                        );
                      })
                      .filter(Boolean); // Remove null entries
                  })()}

                {/* Node load indicators (Type 1 moments as circular rings; Type 2 perpendicular arrows) */}
                <NodeLoadIndicators
                  nodes={nodes}
                  nodeMomentLoads={uiStore.nodeMomentLoads || {}}
                  nodePerpLoads={uiStore.nodePerpLoads || {}}
                  scaleFactor={scaleFactor}
                />
              </Canvas3D>
            </div>
          </Panel>

          {/* Context Menu for nodes/beams */}
          <ContextMenu
            x={uiStore.contextMenuPosition.x}
            y={uiStore.contextMenuPosition.y}
            visible={uiStore.contextMenuVisible}
            onClose={() => uiStore.closeContextMenu()}
            items={((): ContextMenuItem[] => {
              return [
                {
                  id: "type1",
                  label: "Type 1 Load (Moment)",
                  action: () => {
                    uiStore.setSelectedLoadType(1);
                    // Open modal
                    uiStore.openModal && uiStore.openModal("node-load-input");
                  },
                },
                {
                  id: "type2",
                  label: "Type 2 Load (Perpendicular)",
                  action: () => {
                    uiStore.setSelectedLoadType(2);
                    uiStore.openModal && uiStore.openModal("node-load-input");
                  },
                },
              ];
            })()}
          />

          {/* Load input modal (reusable) */}
          <LoadInputModal
            isOpen={uiStore.activeModal === "node-load-input"}
            title={
              uiStore.selectedLoadType === 1
                ? "Type 1 Load (Moment)"
                : "Type 2 Load (Perpendicular)"
            }
            initial={(() => {
              const nodeId = uiStore.selectedNodeId;
              if (!nodeId) return { x: 0, y: 0, z: 0 };
              // Provide initial values depending on selected load type
              if (uiStore.selectedLoadType === 1) {
                const v =
                  uiStore.nodeMomentLoads && uiStore.nodeMomentLoads[nodeId];
                return v ? { x: v.x, y: v.y, z: v.z } : { x: 0, y: 0, z: 0 };
              } else {
                const v =
                  uiStore.nodePerpLoads && uiStore.nodePerpLoads[nodeId];
                return v ? { x: v.x, y: v.y, z: v.z } : { x: 0, y: 0, z: 0 };
              }
            })()}
            onClose={() => uiStore.closeModal && uiStore.closeModal()}
            onSave={(values) => {
              const nodeId = uiStore.selectedNodeId;
              if (!nodeId) return;
              const allZero =
                Number(values.x) === 0 &&
                Number(values.y) === 0 &&
                Number(values.z) === 0;
              if (uiStore.selectedLoadType === 1) {
                if (allZero) {
                  uiStore.removeNodeMomentLoad(nodeId);
                  uiStore.showNotification &&
                    uiStore.showNotification(
                      "info",
                      "Moment Load",
                      "Cleared moment load (all values 0)"
                    );
                } else {
                  uiStore.setNodeMomentLoad(nodeId, {
                    x: Number(values.x),
                    y: Number(values.y),
                    z: Number(values.z),
                  });
                  uiStore.showNotification &&
                    uiStore.showNotification(
                      "success",
                      "Moment Load",
                      `Saved Type 1 (Moment): X=${values.x}, Y=${values.y}, Z=${values.z}`
                    );
                }
              } else {
                if (allZero) {
                  uiStore.removeNodePerpLoad(nodeId);
                  uiStore.showNotification &&
                    uiStore.showNotification(
                      "info",
                      "Perp Load",
                      "Cleared perpendicular load (all values 0)"
                    );
                } else {
                  uiStore.setNodePerpLoad(nodeId, {
                    x: Number(values.x),
                    y: Number(values.y),
                    z: Number(values.z),
                  });
                  uiStore.showNotification &&
                    uiStore.showNotification(
                      "success",
                      "Perp Load",
                      `Saved Type 2 (Perpendicular): X=${values.x}, Y=${values.y}, Z=${values.z}`
                    );
                }
              }
              // close modal and menu
              uiStore.closeModal && uiStore.closeModal();
              uiStore.closeContextMenu();
            }}
          />

          {/* Right Panel - Information Tables */}
          <Panel defaultSize={20} minSize={15}>
            <div className="h-full bg-white dark:bg-gray-900 flex flex-col overflow-y-auto overflow-x-hidden">
              <div
                className="flex-1 flex flex-col overflow-y-auto"
                style={{ paddingBottom: "115px" }}
              >
                {state.showAnalysisPanel && (
                  <CollapsiblePanel
                    title="Structural Analysis"
                    defaultExpanded={true}
                  >
                    <div className="p-2">
                      <AnalysisPanel />
                    </div>
                  </CollapsiblePanel>
                )}

                <CollapsiblePanel title="Nodes" defaultExpanded={false}>
                  <div className="p-2">
                    <NodesDataTable />
                  </div>
                </CollapsiblePanel>

                <CollapsiblePanel title="Beams" defaultExpanded={false}>
                  <div className="p-2">
                    <BeamsDataTable />
                  </div>
                </CollapsiblePanel>

                <CollapsiblePanel title="Supports" defaultExpanded={false}>
                  <div className="p-2">
                    <SupportsDataTable />
                  </div>
                </CollapsiblePanel>

                <CollapsiblePanel
                  title="Section Properties"
                  defaultExpanded={false}
                >
                  <div className="p-2">
                    <SectionPropertiesTable
                      onOpenConfig={() => setIsPropertyDialogOpen(true)}
                    />
                  </div>
                </CollapsiblePanel>

                <CollapsiblePanel title="Loadcases" defaultExpanded={false}>
                  <div className="p-2">
                    <LoadcaseDataTable />
                  </div>
                </CollapsiblePanel>
              </div>
            </div>
          </Panel>
        </PanelGroup>
      </div>

      {/* Truss Configuration Panel */}
      {uiStore.trussConfigPanelVisible &&
        uiStore.trussConfigPanelNodeId &&
        uiStore.trussConfigPanelPosition && (
          <TrussConfigPanel
            nodeId={uiStore.trussConfigPanelNodeId}
            position={uiStore.trussConfigPanelPosition}
            onClose={() => {
              uiStore.closeTrussConfigPanel();
              trussStore.setActiveTool(false);
            }}
          />
        )}

      {/* Beam Truss Configuration Panel */}
      {beamTrussConfigState.isOpen &&
        beamTrussConfigState.beamId &&
        beamTrussConfigState.position && (
          <BeamTrussConfigPanel
            beamId={beamTrussConfigState.beamId}
            position={beamTrussConfigState.position}
            onClose={() => {
              setBeamTrussConfigState({
                isOpen: false,
                beamId: null,
                position: null,
              });
              trussStore.setActiveTool(false);
            }}
          />
        )}

      {/* View Information Panel */}
      <ViewInfoPanel
        currentView={currentView}
        isVisible={isViewInfoPanelVisible}
        onClose={() => setIsViewInfoPanelVisible(false)}
      />

      {/* Status Bar - Essential Info Only (Plane info removed) */}
      <div className="bg-gray-800 text-white px-4 py-2 text-sm flex items-center justify-between">
        <span className="text-gray-300">
          {modelStore.statusMessage && (
            <span className="text-yellow-400">{modelStore.statusMessage}</span>
          )}
        </span>
        <span className="text-gray-400">
          Nodes: {nodes.length} | Beams: {beams.length} | Selected:{" "}
          {selectedNodeIds.length} nodes, {selectedBeamIds.length} beams
          {state.lastCreatedNodeId && (
            <span className="text-green-400 ml-2">
              ✓ Node N{state.lastCreatedNodeId} created
            </span>
          )}
          {canCopyPaste &&
            (selectedNodeIds.length > 0 || selectedBeamIds.length > 0) && (
              <span className="text-blue-400 ml-2">✓ Copy/Paste Ready</span>
            )}
        </span>
      </div>

      {/* Dialogs */}
      <WorkspaceDialogs
        state={state}
        callbacks={callbacks}
        operations={{ nodes: nodeOperations }}
      />

      {/* Offset Input Dialog for Copy-Paste */}
      <OffsetInputDialog
        isOpen={isOffsetDialogOpen}
        onConfirm={pasteWithOffset}
        onCancel={hideOffsetDialog}
        selectedCount={
          typeof selectedCount === "number"
            ? { nodes: selectedCount, beams: selectedCount }
            : selectedCount
        }
      />

      {/* Create Perpendicular Beams Button */}
      {(() => {
        // Calculate valid node count for perpendicular beam creation
        let validNodeCount = 0;

        if (
          modelStore.isPrepMode &&
          modelStore.referenceBeamId &&
          selectedNodeIds.length > 0
        ) {
          const refBeam = beams.find(
            (b) => b.id === modelStore.referenceBeamId
          );
          if (refBeam) {
            const refStartNode = nodes.find(
              (n) => n.id === refBeam.startNodeId
            );
            const refEndNode = nodes.find((n) => n.id === refBeam.endNodeId);

            if (refStartNode && refEndNode) {
              // Count only valid nodes
              validNodeCount = selectedNodeIds.filter((nodeId) => {
                const targetNode = nodes.find((n) => n.id === nodeId);
                if (!targetNode) return false;

                // Check if projection is within beam segment
                const beamStart = new THREE.Vector3(
                  refStartNode.x,
                  refStartNode.y,
                  refStartNode.z
                );
                const beamEnd = new THREE.Vector3(
                  refEndNode.x,
                  refEndNode.y,
                  refEndNode.z
                );
                const nodePoint = new THREE.Vector3(
                  targetNode.x,
                  targetNode.y,
                  targetNode.z
                );

                const beamDirection = beamEnd.clone().sub(beamStart);
                const nodeToStart = nodePoint.clone().sub(beamStart);
                const projection =
                  nodeToStart.dot(beamDirection) / beamDirection.lengthSq();

                return projection > 0 && projection < 1;
              }).length;
            }
          }
        }

        return (
          <CreatePerpBeamButton
            isVisible={
              !!(
                modelStore.isPrepMode &&
                modelStore.referenceBeamId &&
                selectedNodeIds.length > 0
              )
            }
            isDisabled={!modelStore.referenceBeamId || validNodeCount === 0}
            selectedNodeCount={validNodeCount}
            onCreateBeams={() => {
              if (modelStore.referenceBeamId && selectedNodeIds.length > 0) {
                modelStore.createPerpendicularBeams(
                  modelStore.referenceBeamId,
                  selectedNodeIds
                );
                modelStore.cancelPrepMode(); // Exit prep mode after creating beams
              }
            }}
          />
        );
      })()}

      {/* Enhanced Node Interaction Components */}
      <EnhancedNodeInteractionComponents />

      {/* Enhanced Beam Interaction Components */}
      <EnhancedBeamInteractionComponents />

      {/* Section Properties Configuration Dialog */}
      <SectionPropertiesConfigDialog
        isOpen={isPropertyDialogOpen}
        onClose={() => setIsPropertyDialogOpen(false)}
      />
    </div>
  );
};
